/**
 * Level13Scene - 关卡13：长度收缩
 * @description 教学目标：理解高速运动时长度缩短
 * 核心公式：L' = L/γ（洛伦兹长度收缩）
 */

import Phaser from 'phaser';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { Relativity, SPEED_OF_LIGHT } from '@/physics/Relativity.ts';
import { type RelativityEffectConfig } from '@/shaders/RelativityShader.ts';

// 关卡13特定配置
interface Level13SpecificConfig {
  /** 物体本征长度（静止时长度） */
  properLength: number;
  /** 门洞宽度（收缩后需要通过的宽度） */
  gateWidth: number;
  /** 可选速度列表 */
  velocityOptions: number[];
  /** 物体起始位置 x */
  objectStartX: number;
  /** 物体起始位置 y */
  objectStartY: number;
  /** 门洞位置 x */
  gateX: number;
  /** 门洞位置 y */
  gateY: number;
  /** 目标区域位置 x */
  targetX: number;
  /** 目标区域位置 y */
  targetY: number;
  /** 目标区域半径 */
  targetRadius: number;
  /** 公式 */
  formula: string;
  /** 提示 */
  hint: string;
}

// 游戏状态
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

/**
 * Level13Scene 类
 * 关卡13场景，实现长度收缩教学
 */
export class Level13Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level13SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;

  // 相对论计算模块
  private relativity: Relativity;

  // 游戏对象
  private movingObject: Phaser.GameObjects.Rectangle | null = null;
  private originalLength: number = 100;
  private contractedLength: number = 100;
  private selectedVelocity: number = 0;
  private objectVelocity: number = 0; // 当前运动速度

  // 门洞和目标
  private gateGraphics: Phaser.GameObjects.Graphics | null = null;
  private targetZone: Phaser.GameObjects.Graphics | null = null;
  private trajectoryLine: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private hintText: Phaser.GameObjects.Text | null = null;
  private velocityButtons: Phaser.GameObjects.Graphics[] = [];
  private startButton: Phaser.GameObjects.Graphics | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private gammaText: Phaser.GameObjects.Text | null = null;
  private lengthText: Phaser.GameObjects.Text | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  // Shader 应用状态
  private shaderApplied: boolean = false;

  constructor() {
    super({ key: 'Level13Scene' });
    // 初始化相对论计算模块（使用游戏简化单位 c=1）
    this.relativity = new Relativity(SPEED_OF_LIGHT);
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-13', 'src/levels/levels/level-13.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-13') as LevelConfig;

    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level13SpecificConfig;
      this.originalLength = this.levelSpecific.properLength;
    }

    // 创建背景
    this.createBackground();

    // 创建游戏场景
    this.createGameScene();

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();

    // 注册 Shader（如果 WebGL 可用）
    this.registerShader();
  }

  override update(time: number, delta: number): void {
    if (this.gameState !== 'running') return;

    // 更新时间
    this.elapsedTime += delta / 1000;

    // 更新物体位置
    this.updateObjectPosition(delta);

    // 更新长度收缩视觉效果
    this.updateLengthContractionEffect();

    // 绘制轨迹
    this.drawTrajectory();

    // 检测游戏结束
    this.checkGameEnd();

    // 更新 Shader 时间
    if (this.shaderApplied && this.movingObject) {
      const config: RelativityEffectConfig = {
        velocity: this.objectVelocity / SPEED_OF_LIGHT,
        direction: { x: 1, y: 0 },
        time: time / 1000,
      };
      // 尝试更新 shader 参数
      this.updateShaderConfig(config);
    }
  }

  /**
   * 创建背景
   */
  private createBackground(): void {
    // 深色宇宙背景
    this.cameras.main.setBackgroundColor('#0a0a1a');

    // 添加星空效果（简单的随机点）
    const starGraphics = this.add.graphics();
    for (let i = 0; i < 50; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const alpha = Math.random() * 0.5 + 0.2;
      starGraphics.fillStyle(0xffffff, alpha);
      starGraphics.fillCircle(x, y, 1);
    }

    // 添加网格线作为参考（代表静止参考系）
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x1a1a3a, 0.2);

    // 垂直网格线
    for (let x = 0; x <= GAME_WIDTH; x += 40) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    // 水平网格线
    for (let y = 0; y <= GAME_HEIGHT; y += 40) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }

    // 添加速度标尺区域背景
    const scaleBg = this.add.graphics();
    scaleBg.fillStyle(0x1a1a3a, 0.5);
    scaleBg.fillRect(50, 100, 150, 350);
    scaleBg.lineStyle(2, 0x4a4a6a);
    scaleBg.strokeRect(50, 100, 150, 350);
  }

  /**
   * 创建游戏场景
   */
  private createGameScene(): void {
    if (!this.levelSpecific) return;

    // 门洞（需要收缩后才能通过）
    this.gateGraphics = this.add.graphics();
    const gateWidth = this.levelSpecific.gateWidth;
    const gateHeight = 120;
    const gateX = this.levelSpecific.gateX;
    const gateY = GAME_HEIGHT - this.levelSpecific.gateY;

    // 门洞框架（红色表示障碍）
    this.gateGraphics.fillStyle(0x4a2a2a, 1);
    // 左边门柱
    this.gateGraphics.fillRect(gateX - gateWidth / 2 - 20, gateY - gateHeight / 2, 20, gateHeight);
    // 右边门柱
    this.gateGraphics.fillRect(gateX + gateWidth / 2, gateY - gateHeight / 2, 20, gateHeight);
    // 门洞开口（绿色表示可通过）
    this.gateGraphics.fillStyle(0x2a4a2a, 0.3);
    this.gateGraphics.fillRect(gateX - gateWidth / 2, gateY - gateHeight / 2, gateWidth, gateHeight);

    // 门洞标签
    this.add.text(gateX, gateY - gateHeight / 2 - 15, `门洞宽 ${gateWidth}`, {
      fontSize: '12px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 目标区域
    this.targetZone = this.add.graphics();
    this.targetZone.fillStyle(0x4ade80, 0.3);
    this.targetZone.fillCircle(
      this.levelSpecific.targetX,
      GAME_HEIGHT - this.levelSpecific.targetY,
      this.levelSpecific.targetRadius
    );
    this.targetZone.lineStyle(3, 0x4ade80);
    this.targetZone.strokeCircle(
      this.levelSpecific.targetX,
      GAME_HEIGHT - this.levelSpecific.targetY,
      this.levelSpecific.targetRadius
    );

    // 目标标签
    this.add.text(
      this.levelSpecific.targetX,
      GAME_HEIGHT - this.levelSpecific.targetY + this.levelSpecific.targetRadius + 20,
      '目标区域',
      {
        fontSize: '14px',
        color: '#4ade80',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 运动物体（矩形）
    const objectX = this.levelSpecific.objectStartX;
    const objectY = GAME_HEIGHT - this.levelSpecific.objectStartY;
    this.movingObject = this.add.rectangle(
      objectX,
      objectY,
      this.originalLength,
      30,
      0x6b8cff
    );
    this.movingObject.setOrigin(0.5);

    // 物体起始位置标记
    const startMarker = this.add.graphics();
    startMarker.lineStyle(2, 0xffa500);
    startMarker.strokeCircle(objectX, objectY, 20);

    this.add.text(objectX, objectY - 40, '起点', {
      fontSize: '12px',
      color: '#ffa500',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 物体长度标签（初始）
    this.add.text(objectX, objectY + 25, `L = ${this.originalLength}`, {
      fontSize: '10px',
      color: '#6b8cff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 初始化轨迹线
    this.trajectoryLine = this.add.graphics();
    this.trajectoryLine.setDepth(4);
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 公式显示
    this.add.text(GAME_WIDTH / 2, 30, '长度收缩公式：L\' = L/γ', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      60,
      this.levelSpecific?.hint ?? '选择速度，观察物体长度收缩效应',
      {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 洛伦兹因子显示
    this.gammaText = this.add.text(70, 120, 'γ = 1.00', {
      fontSize: '16px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    // 收缩后长度显示
    this.lengthText = this.add.text(70, 150, 'L\' = 100', {
      fontSize: '16px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    // 速度标尺
    this.createSpeedScale();

    // 速度选择按钮
    this.createVelocityButtons();

    // 开始按钮
    this.createStartButton();

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '28px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);

    // 物理说明区域
    this.createPhysicsInfo();
  }

  /**
   * 创建速度标尺
   */
  private createSpeedScale(): void {
    // 速度标尺标题
    this.add.text(70, 180, '速度 v/c', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 标尺刻度
    const scaleY = 200;
    const scaleHeight = 200;

    const scaleGraphics = this.add.graphics();
    scaleGraphics.lineStyle(2, 0x4a4a6a);
    scaleGraphics.beginPath();
    scaleGraphics.moveTo(80, scaleY);
    scaleGraphics.lineTo(80, scaleY + scaleHeight);
    scaleGraphics.strokePath();

    // 刻度标记
    const speeds = [0, 0.25, 0.5, 0.75, 0.9, 0.95];
    speeds.forEach((speed, index) => {
      const y = scaleY + index * (scaleHeight / (speeds.length - 1));
      scaleGraphics.beginPath();
      scaleGraphics.moveTo(75, y);
      scaleGraphics.lineTo(85, y);
      scaleGraphics.strokePath();

      this.add.text(90, y, `${speed.toFixed(2)}c`, {
        fontSize: '10px',
        color: '#6a6a8a',
        fontFamily: 'Arial',
      }).setOrigin(0, 0.5);
    });

    // 光速标记
    this.add.text(100, scaleY + scaleHeight + 10, 'c = 光速', {
      fontSize: '10px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
    });
  }

  /**
   * 创建速度选择按钮
   */
  private createVelocityButtons(): void {
    if (!this.levelSpecific) return;

    const velocities = this.levelSpecific.velocityOptions;
    const startX = GAME_WIDTH / 2 - (velocities.length * 80) / 2;
    const y = 520;

    this.add.text(GAME_WIDTH / 2, y - 30, '选择物体速度 v:', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    velocities.forEach((velocity, i) => {
      const x = startX + i * 80 + 40;
      const btn = this.add.graphics();
      const isSelected = this.selectedVelocity === velocity;
      const ratio = velocity / SPEED_OF_LIGHT;

      // 根据速度等级选择颜色
      let color: number;
      if (ratio < 0.3) {
        color = 0x4a4a6a; // 低速 - 灰色
      } else if (ratio < 0.6) {
        color = 0x6b8cff; // 中速 - 蓝色
      } else if (ratio < 0.8) {
        color = 0xffa500; // 高速 - 橙色
      } else {
        color = 0xff6b6b; // 超高速 - 红色
      }

      btn.fillStyle(isSelected ? color : 0x3a3a5a, isSelected ? 1 : 0.8);
      btn.fillRect(x - 35, y, 70, 40);
      btn.lineStyle(2, isSelected ? color : 0x5a5a7a);
      btn.strokeRect(x - 35, y, 70, 40);

      // 显示速度（以光速为单位）
      this.add.text(x, y + 20, `${ratio.toFixed(2)}c`, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);

      btn.setInteractive(
        new Phaser.Geom.Rectangle(x - 35, y, 70, 40),
        Phaser.Geom.Rectangle.Contains
      );

      btn.on('pointerdown', () => {
        if (this.gameState === 'idle') {
          this.selectedVelocity = velocity;
          this.updateVelocityButtons();
          this.updateLengthDisplay();
          this.updateHint();
        }
      });

      btn.on('pointerover', () => {
        if (this.gameState === 'idle') {
          btn.clear();
          const ratio = velocity / SPEED_OF_LIGHT;
          let hoverColor: number;
          if (ratio < 0.3) {
            hoverColor = 0x5a5a7a;
          } else if (ratio < 0.6) {
            hoverColor = 0x7b9cff;
          } else if (ratio < 0.8) {
            hoverColor = 0xffb520;
          } else {
            hoverColor = 0xff7b7b;
          }
          btn.fillStyle(hoverColor, 0.9);
          btn.fillRect(x - 35, y, 70, 40);
          btn.strokeRect(x - 35, y, 70, 40);
        }
      });

      btn.on('pointerout', () => {
        if (this.gameState === 'idle') {
          this.updateVelocityButtons();
        }
      });

      this.velocityButtons.push(btn);
    });
  }

  /**
   * 更新速度按钮状态
   */
  private updateVelocityButtons(): void {
    if (!this.levelSpecific) return;

    const velocities = this.levelSpecific.velocityOptions;
    const startX = GAME_WIDTH / 2 - (velocities.length * 80) / 2;
    const y = 520;

    this.velocityButtons.forEach((btn, i) => {
      const velocity = velocities[i];
      if (velocity === undefined) return;
      const x = startX + i * 80 + 40;
      const isSelected = this.selectedVelocity === velocity;
      const ratio = velocity / SPEED_OF_LIGHT;

      let color: number;
      if (ratio < 0.3) {
        color = 0x4a4a6a;
      } else if (ratio < 0.6) {
        color = 0x6b8cff;
      } else if (ratio < 0.8) {
        color = 0xffa500;
      } else {
        color = 0xff6b6b;
      }

      btn.clear();
      btn.fillStyle(isSelected ? color : 0x3a3a5a, isSelected ? 1 : 0.8);
      btn.fillRect(x - 35, y, 70, 40);
      btn.lineStyle(2, isSelected ? color : 0x5a5a7a);
      btn.strokeRect(x - 35, y, 70, 40);
    });
  }

  /**
   * 更新长度显示
   */
  private updateLengthDisplay(): void {
    if (this.selectedVelocity === 0) {
      this.gammaText?.setText('γ = 1.00');
      this.lengthText?.setText(`L' = ${this.originalLength}`);
      this.contractedLength = this.originalLength;
      return;
    }

    // 计算洛伦兹因子和收缩长度
    const result = this.relativity.lengthContraction(this.originalLength, this.selectedVelocity);
    this.contractedLength = result.value;

    this.gammaText?.setText(`γ = ${result.gamma.toFixed(2)}`);
    this.lengthText?.setText(`L' = ${this.contractedLength.toFixed(1)}`);

    // 更新物体宽度显示
    if (this.movingObject) {
      this.movingObject.setSize(this.contractedLength, 30);
    }
  }

  /**
   * 更新提示文本
   */
  private updateHint(): void {
    if (!this.levelSpecific || this.selectedVelocity === 0) return;

    const result = this.relativity.lengthContraction(this.originalLength, this.selectedVelocity);
    const gateWidth = this.levelSpecific.gateWidth;

    if (this.contractedLength <= gateWidth) {
      this.hintText?.setText(
        `速度 ${(this.selectedVelocity / SPEED_OF_LIGHT).toFixed(2)}c → γ=${result.gamma.toFixed(2)}，长度收缩后 L'=${this.contractedLength.toFixed(1)} ≤ 门洞宽${gateWidth}，可通过！`
      );
    } else {
      this.hintText?.setText(
        `速度 ${(this.selectedVelocity / SPEED_OF_LIGHT).toFixed(2)}c → γ=${result.gamma.toFixed(2)}，长度收缩后 L'=${this.contractedLength.toFixed(1)} > 门洞宽${gateWidth}，无法通过！`
      );
    }
  }

  /**
   * 创建物理信息显示
   */
  private createPhysicsInfo(): void {
    // 洛伦兹因子公式
    this.add.text(50, GAME_HEIGHT - 80, 'γ = 1/√(1 - v²/c²)', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 长度收缩公式
    this.add.text(200, GAME_HEIGHT - 80, 'L\' = L/γ', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 光速限制说明
    this.add.text(350, GAME_HEIGHT - 80, 'v < c（速度永远小于光速）', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 教学说明
    this.add.text(50, GAME_HEIGHT - 50, '高速运动时，物体在运动方向上会变短！', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    });
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRect(GAME_WIDTH - 140, 520, 120, 50);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRect(GAME_WIDTH - 140, 520, 120, 50);

    this.add.text(GAME_WIDTH - 80, 545, '开始', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(GAME_WIDTH - 140, 520, 120, 50),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x5aee90, 1);
      this.startButton?.fillRect(GAME_WIDTH - 140, 520, 120, 50);
      this.startButton?.strokeRect(GAME_WIDTH - 140, 520, 120, 50);
    });

    this.startButton.on('pointerout', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x4ade80, 1);
      this.startButton?.fillRect(GAME_WIDTH - 140, 520, 120, 50);
      this.startButton?.strokeRect(GAME_WIDTH - 140, 520, 120, 50);
    });
  }

  /**
   * 注册 Shader
   */
  private registerShader(): void {
    // 检查是否是 WebGL 渲染器
    if (this.game.renderer.type !== Phaser.WEBGL) {
      console.warn('[Level13Scene] Canvas 渲染器，Shader 效果不可用');
      return;
    }

    try {
      // Shader 已在 main.ts 中注册，这里只是标记
      this.shaderApplied = true;
      console.log('[Level13Scene] RelativityShader 可用');
    } catch (e) {
      console.warn('[Level13Scene] Shader 注册失败', e);
    }
  }

  /**
   * 更新 Shader 配置
   */
  private updateShaderConfig(_config: RelativityEffectConfig): void {
    // 由于 Phaser 3 的 Pipeline 系统，我们需要通过 PostFX 来应用
    // 这里我们直接更新物体尺寸来模拟效果
    // 真正的 Shader 效果需要在 Pipeline Pipeline 中处理
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key === 'Enter') {
        this.startGame();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetGame();
      }
    });
  }

  /**
   * 更新物体位置
   */
  private updateObjectPosition(delta: number): void {
    if (!this.movingObject || !this.levelSpecific) return;

    // 使用选定的速度移动
    const velocity = this.selectedVelocity;
    const dt = delta / 1000;

    // 获取当前位置
    const currentX = this.movingObject.x;

    // 更新位置（向右移动）
    const newX = currentX + velocity * dt * 100; // 缩放因子使运动可见

    this.movingObject.setX(newX);

    // 更新存储的速度
    this.objectVelocity = velocity;
  }

  /**
   * 更新长度收缩视觉效果
   */
  private updateLengthContractionEffect(): void {
    if (!this.movingObject) return;

    // 计算当前速度下的收缩长度
    const result = this.relativity.lengthContraction(this.originalLength, this.objectVelocity);

    // 更新物体尺寸
    this.movingObject.setSize(result.value, 30);

    // 更新显示文本
    this.gammaText?.setText(`γ = ${result.gamma.toFixed(2)}`);
    this.lengthText?.setText(`L' = ${result.value.toFixed(1)}`);
  }

  /**
   * 绘制运动轨迹
   */
  private drawTrajectory(): void {
    if (!this.movingObject || !this.trajectoryLine) return;

    const x = this.movingObject.x;
    const y = this.movingObject.y;

    // 使用点来绘制轨迹（每帧添加一个点）
    this.trajectoryLine.fillStyle(0x6b8cff, 0.3);
    this.trajectoryLine.fillCircle(x, y, 2);
  }

  /**
   * 开始游戏
   */
  private startGame(): void {
    if (this.gameState !== 'idle') return;
    if (this.selectedVelocity === 0) {
      this.hintText?.setText('请先选择物体速度！');
      return;
    }

    if (!this.levelSpecific) return;

    // 检查是否能通过门洞
    if (this.contractedLength > this.levelSpecific.gateWidth) {
      this.hintText?.setText('长度收缩不足！选择更高的速度来通过门洞！');
      return;
    }

    this.gameState = 'running';
    this.elapsedTime = 0;

    // 清除轨迹
    if (this.trajectoryLine) {
      this.trajectoryLine.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 更新开始按钮状态
    this.updateStartButtonState('running');

    // 更新提示
    this.hintText?.setText('观察物体长度收缩效果...');
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
    } else {
      this.startButton.fillStyle(0x4ade80, 1);
    }
    this.startButton.fillRect(GAME_WIDTH - 140, 520, 120, 50);
    this.startButton.strokeRect(GAME_WIDTH - 140, 520, 120, 50);
  }

  /**
   * 检测游戏结束
   */
  private checkGameEnd(): void {
    if (!this.movingObject || !this.levelSpecific) return;

    const targetX = this.levelSpecific.targetX;
    const targetY = this.levelSpecific.targetY;
    const targetRadius = this.levelSpecific.targetRadius;
    const gateX = this.levelSpecific.gateX;
    const gateWidth = this.levelSpecific.gateWidth;

    const objectX = this.movingObject.x;
    const objectY = GAME_HEIGHT - this.movingObject.y; // 转换坐标系

    // 检测是否到达目标
    const dx = objectX - targetX;
    const dy = objectY - targetY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= targetRadius) {
      this.gameState = 'success';
      this.showSuccessResult();
      this.updateStartButtonState('idle');
      return;
    }

    // 检测是否撞到门柱（门洞两侧）
    const gateY = this.levelSpecific.gateY;
    const gateHeight = 120;

    // 检查是否在门洞高度范围内
    if (objectY >= gateY - gateHeight / 2 && objectY <= gateY + gateHeight / 2) {
      // 检查是否撞到左门柱
      if (objectX - this.contractedLength / 2 < gateX - gateWidth / 2 - 20) {
        this.gameState = 'failed';
        this.showFailResult('撞到门柱！选择更高的速度！');
        this.updateStartButtonState('idle');
        return;
      }

      // 检查是否撞到右门柱
      if (objectX + this.contractedLength / 2 > gateX + gateWidth / 2 + 20) {
        this.gameState = 'failed';
        this.showFailResult('撞到门柱！速度太高？');
        this.updateStartButtonState('idle');
        return;
      }
    }

    // 检测是否超出边界
    if (objectX > GAME_WIDTH + 50 || objectX < -50) {
      this.gameState = 'failed';
      this.showFailResult('物体超出边界！');
      this.updateStartButtonState('idle');
    }
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(13, this.elapsedTime);

    const ratio = this.selectedVelocity / SPEED_OF_LIGHT;
    const gamma = this.relativity.calculateGamma(this.selectedVelocity);

    this.resultText?.setText(`🎉 成功！用时 ${this.elapsedTime.toFixed(2)}秒`)
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(
      `速度 ${ratio.toFixed(2)}c → γ=${gamma.toFixed(2)}，长度收缩成功！L'=${this.contractedLength.toFixed(1)} 通过了门洞！`
    );

    this.createCompletionButtons();
  }

  /**
   * 创建完成后的按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 60;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(14)) {
        // Level14Scene 还未实现，返回关卡选择
        this.scene.start('LevelSelectScene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * 显示失败结果
   */
  private showFailResult(reason: string): void {
    this.resultText?.setText(`❌ ${reason}`)
      .setColor('#ff6b6b')
      .setVisible(true);

    if (reason.includes('门柱')) {
      this.hintText?.setText('提示：需要更高的速度才能让物体收缩到能通过门洞！');
    }
  }

  /**
   * 重置游戏
   */
  private resetGame(): void {
    if (!this.levelSpecific) return;

    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.selectedVelocity = 0;
    this.objectVelocity = 0;
    this.contractedLength = this.originalLength;

    // 重置物体位置
    if (this.movingObject) {
      this.movingObject.setX(this.levelSpecific.objectStartX);
      this.movingObject.setSize(this.originalLength, 30);
    }

    // 清除轨迹
    if (this.trajectoryLine) {
      this.trajectoryLine.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '选择速度，观察物体长度收缩效应');

    // 重置显示
    this.gammaText?.setText('γ = 1.00');
    this.lengthText?.setText(`L' = ${this.originalLength}`);

    // 更新按钮状态
    this.updateStartButtonState('idle');
    this.updateVelocityButtons();
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-13');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 13,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前时间
   */
  getElapsedTime(): number {
    return this.elapsedTime;
  }

  /**
   * 获取物体位置
   */
  getObjectPosition(): { x: number; y: number } | null {
    return this.movingObject ? { x: this.movingObject.x, y: this.movingObject.y } : null;
  }

  /**
   * 获取选中的速度
   */
  getSelectedVelocity(): number {
    return this.selectedVelocity;
  }

  /**
   * 获取收缩后的长度
   */
  getContractedLength(): number {
    return this.contractedLength;
  }

  /**
   * 获取洛伦兹因子
   */
  getGamma(): number {
    if (this.selectedVelocity === 0) return 1;
    return this.relativity.calculateGamma(this.selectedVelocity);
  }
}